import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MoneyIcon from '@mui/icons-material/Money';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import accountingService from '../../services/accountingService';
import ownAuthSession from '../../services/auth/ownAuthSession';

/**
 * Ödeme yöntemi satırlarındaki "Bağlantı" alanının yanına yerleşen hızlı kayıt
 * butonları. Kullanıcıyı Kasa / Banka modülüne göndermeden, satış ekranının
 * şubesine bağlı bir nakit kasası veya POS banka hesabı oluşturur.
 *
 * Kayıt payload'ları Cashbox.tsx ve AddBankAccount.tsx ile aynıdır; buradaki tek
 * fark sorulan alan sayısının en aza indirilmesidir (para birimi TRY, IBAN boş,
 * POS işareti banka hesabında otomatik).
 */

export interface CreatedCashbox {
  id: string;
  name: string;
}

export interface CreatedBankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
}

/** AuthContext şubesiz kayıtlar için "null" kimliğini bir sözde şube olarak tutar. */
const isRealBranchId = (branchId?: string | null): boolean =>
  !!branchId && branchId !== 'null';

/** Verilen şube kimliğinin adını AuthContext'teki şube listesinden çözer. */
export const useBranchName = (branchId?: string | null): string | null => {
  const { branches } = useAuth();
  return useMemo(() => {
    if (!isRealBranchId(branchId)) return null;
    return branches.find((b) => b.id === branchId)?.name || null;
  }, [branches, branchId]);
};

const normalizeTr = (value: string): string => value.trim().toLocaleLowerCase('tr');

const createdBy = (): string => ownAuthSession.getCurrentEmail() || 'System';

interface FeedbackState {
  severity: 'success' | 'info' | 'error';
  message: string;
}

interface QuickCreateCashboxButtonProps {
  companyId: string | null | undefined;
  branchId: string | null | undefined;
  /** Tekrar kaydı önlemek ve mevcut kasayı seçmek için kullanılan liste. */
  existingCashboxes: CreatedCashbox[];
  /**
   * Verilirse diyalog açılmaz; kasa doğrudan bu adla oluşturulur.
   * "Diğer ödemeler" satırlarında şube + ödeme adından üretilen isim için kullanılır.
   */
  autoName?: string | null;
  /** Diyalogda ön-dolu gelecek isim önerisi (autoName verilmediğinde). */
  suggestedName?: string | null;
  /** Şube dışındaki nedenlerle butonun kapatılması gerekiyorsa (örn. ödeme adı seçilmemiş). */
  disabled?: boolean;
  disabledReason?: string;
  onCreated: (cashbox: CreatedCashbox) => void;
}

export const QuickCreateCashboxButton: React.FC<QuickCreateCashboxButtonProps> = ({
  companyId,
  branchId,
  existingCashboxes,
  autoName,
  suggestedName,
  disabled,
  disabledReason,
  onCreated,
}) => {
  const { t } = useTranslation('salesSystem');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);

  const branchMissing = !isRealBranchId(branchId) || !companyId;
  const isDisabled = branchMissing || !!disabled || saving;
  const tooltip = branchMissing
    ? t('quickCreatePaymentTarget.branchRequired')
    : disabled && disabledReason
      ? disabledReason
      : t('quickCreatePaymentTarget.cashbox.tooltip');

  const create = async (cashboxName: string): Promise<void> => {
    if (!companyId) return;
    const existing = existingCashboxes.find((cb) => normalizeTr(cb.name) === normalizeTr(cashboxName));
    if (existing) {
      onCreated(existing);
      setFeedback({
        severity: 'info',
        message: t('quickCreatePaymentTarget.cashbox.alreadyExists', { name: existing.name }),
      });
      return;
    }

    setSaving(true);
    try {
      const created = await accountingService.createCashbox({
        companyId,
        name: cashboxName,
        branchId,
        createdBy: createdBy(),
      });
      const createdId = (created?.id ?? created?.Id)?.toString();
      if (!createdId) {
        throw new Error(t('quickCreatePaymentTarget.errors.missingId'));
      }
      onCreated({ id: createdId, name: cashboxName });
      setFeedback({
        severity: 'success',
        message: t('quickCreatePaymentTarget.cashbox.created', { name: cashboxName }),
      });
      setDialogOpen(false);
      setName('');
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        t('quickCreatePaymentTarget.errors.cashboxFailed');
      if (dialogOpen) {
        setDialogError(message);
      } else {
        setFeedback({ severity: 'error', message });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClick = () => {
    if (autoName) {
      void create(autoName);
      return;
    }
    setName(suggestedName || '');
    setDialogError(null);
    setDialogOpen(true);
  };

  return (
    <>
      <Tooltip title={tooltip} arrow>
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={handleClick}
            disabled={isDisabled}
            aria-label={t('quickCreatePaymentTarget.cashbox.tooltip')}
          >
            {saving && autoName ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon color="success" />
          {t('quickCreatePaymentTarget.cashbox.dialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('quickCreatePaymentTarget.cashbox.dialogDescription')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('quickCreatePaymentTarget.cashbox.nameLabel')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDialogError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) void create(name.trim());
            }}
            inputProps={{ maxLength: 100 }}
          />
          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit" disabled={saving}>
            {t('quickCreatePaymentTarget.cancel')}
          </Button>
          <Button
            onClick={() => void create(name.trim())}
            variant="contained"
            disabled={!name.trim() || saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {t('quickCreatePaymentTarget.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!feedback}
        autoHideDuration={4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback?.severity || 'info'} onClose={() => setFeedback(null)}>
          {feedback?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

interface QuickCreateBankAccountButtonProps {
  companyId: string | null | undefined;
  branchId: string | null | undefined;
  /** Tekrar kaydı önlemek ve mevcut hesabı seçmek için kullanılan liste. */
  existingBankAccounts: CreatedBankAccount[];
  suggestedBankName?: string | null;
  onCreated: (bankAccount: CreatedBankAccount) => void;
}

/** Banka modülüyle aynı kural: sadece rakam ve en az 5 hane. */
const isValidAccountNumber = (accountNumber: string): boolean =>
  /^\d{5,}$/.test(accountNumber.replace(/\s/g, ''));

export const QuickCreateBankAccountButton: React.FC<QuickCreateBankAccountButtonProps> = ({
  companyId,
  branchId,
  existingBankAccounts,
  suggestedBankName,
  onCreated,
}) => {
  const { t } = useTranslation('salesSystem');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<FeedbackState | null>(null);

  const branchMissing = !isRealBranchId(branchId) || !companyId;
  const accountNumberTouched = accountNumber.trim() !== '';
  const accountNumberInvalid = accountNumberTouched && !isValidAccountNumber(accountNumber);
  const canSubmit = bankName.trim() !== '' && isValidAccountNumber(accountNumber) && !saving;

  const handleCreate = async () => {
    if (!companyId || !canSubmit) return;
    const cleanBankName = bankName.trim();
    const cleanAccountNumber = accountNumber.replace(/\s/g, '');

    const existing = existingBankAccounts.find(
      (ba) =>
        normalizeTr(ba.bankName) === normalizeTr(cleanBankName) &&
        ba.accountNumber.replace(/\s/g, '') === cleanAccountNumber
    );
    if (existing) {
      onCreated(existing);
      setFeedback({
        severity: 'info',
        message: t('quickCreatePaymentTarget.bankAccount.alreadyExists', {
          name: `${existing.bankName} - ${existing.accountNumber}`,
        }),
      });
      setDialogOpen(false);
      return;
    }

    setSaving(true);
    try {
      const created = await accountingService.createBankAccount({
        companyId,
        bankName: cleanBankName,
        accountNumber: cleanAccountNumber,
        branchId,
        isPosAccount: true,
        createdBy: createdBy(),
      });
      const createdId = (created?.id ?? created?.Id)?.toString();
      if (!createdId) {
        throw new Error(t('quickCreatePaymentTarget.errors.missingId'));
      }
      onCreated({ id: createdId, bankName: cleanBankName, accountNumber: cleanAccountNumber });
      setFeedback({
        severity: 'success',
        message: t('quickCreatePaymentTarget.bankAccount.created', {
          name: `${cleanBankName} - ${cleanAccountNumber}`,
        }),
      });
      setDialogOpen(false);
      setBankName('');
      setAccountNumber('');
    } catch (err: any) {
      setDialogError(
        err?.response?.data?.message ||
          err?.message ||
          t('quickCreatePaymentTarget.errors.bankAccountFailed')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Tooltip
        title={
          branchMissing
            ? t('quickCreatePaymentTarget.branchRequired')
            : t('quickCreatePaymentTarget.bankAccount.tooltip')
        }
        arrow
      >
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              setBankName(suggestedBankName || '');
              setAccountNumber('');
              setDialogError(null);
              setDialogOpen(true);
            }}
            disabled={branchMissing}
            aria-label={t('quickCreatePaymentTarget.bankAccount.tooltip')}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceIcon color="primary" />
          {t('quickCreatePaymentTarget.bankAccount.dialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('quickCreatePaymentTarget.bankAccount.dialogDescription')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t('quickCreatePaymentTarget.bankAccount.bankNameLabel')}
              value={bankName}
              onChange={(e) => {
                setBankName(e.target.value);
                setDialogError(null);
              }}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('quickCreatePaymentTarget.bankAccount.accountNumberLabel')}
              value={accountNumber}
              onChange={(e) => {
                setAccountNumber(e.target.value.replace(/\D/g, ''));
                setDialogError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canSubmit) void handleCreate();
              }}
              error={accountNumberInvalid}
              helperText={
                accountNumberInvalid
                  ? t('quickCreatePaymentTarget.bankAccount.accountNumberInvalid')
                  : t('quickCreatePaymentTarget.bankAccount.accountNumberHelper')
              }
              inputProps={{ maxLength: 34, inputMode: 'numeric' }}
            />
          </Stack>
          {dialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {dialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="inherit" disabled={saving}>
            {t('quickCreatePaymentTarget.cancel')}
          </Button>
          <Button
            onClick={() => void handleCreate()}
            variant="contained"
            disabled={!canSubmit}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {t('quickCreatePaymentTarget.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!feedback}
        autoHideDuration={4000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback?.severity || 'info'} onClose={() => setFeedback(null)}>
          {feedback?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

/** "Diğer ödemeler" kasası için isim üretir: "<Şube> <Ödeme Adı> Kasa". */
export const buildOtherPaymentCashboxName = (
  branchName: string | null,
  paymentLabel: string | null | undefined
): string | null => {
  const label = (paymentLabel || '').trim();
  if (!branchName || !label) return null;
  return `${branchName} ${label} Kasa`;
};

/** Nakit kasası için diyalogda önerilecek isim. */
export const buildCashCashboxName = (branchName: string | null): string | null =>
  branchName ? `${branchName} Nakit Kasa` : null;

/** POS banka hesabı için diyalogda önerilecek banka adı. */
export const buildPosBankName = (branchName: string | null): string | null =>
  branchName ? `${branchName} POS` : null;
